/**
 * ExchangeRatesPage.tsx
 * Admin page for the lab's current FX rates (Admin → Exchange Rates).
 *
 * Route: /admin/exchange-rates (rendered inside AdminLayout)
 *
 * Simple per-currency current-rate table (no period/date-range editor). One row
 * per currency showing the current rate-to-CAD, an inline editor + Save, the
 * relationship label "1 {CUR} = {rate} CAD", when it was last updated, and a
 * remove action for non-default currencies. The first row is the read-only CAD
 * base (1.0000). History is preserved server-side: saving today's rate closes
 * the prior period and opens a new one — the admin only ever sees "the current
 * rate".
 *
 * Direction: RateToCAD = CAD per 1 unit of currency (so 1 USD = 1.36 CAD).
 *
 * Write actions (Save / Add / Remove) require admin+; read is open to any
 * authenticated user, mirroring the backend role gate.
 */

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchCurrentRates,
  setCurrentRate,
  removeCurrencyRate,
} from '../../../services/scheduleExchangeRates';
import type { CurrentExchangeRate } from '../../../services/scheduleExchangeRates';
import { usePermissions } from '../../../hooks/usePermissions';
import { roleAtLeast } from '../../../constants/roles';
import { ConfirmDialog, CONFIRM_INITIAL } from '../../../components/shared/ConfirmDialog';
import type { ConfirmDialogState } from '../../../components/shared/ConfirmDialog';
import { ExchangeRateRow } from './ExchangeRateRow';
import { AddCurrencyDialog } from './AddCurrencyDialog';
import {
  BASE_CURRENCY,
  isDefaultCurrency,
  sortForDisplay,
} from './exchangeRateConstants';

/** Pull a friendly message off an axios-style error (400 body or fallback). */
function errorMessage(err: unknown, fallback: string): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = err as any;
  return e?.response?.data?.error ?? e?.response?.data?.message ?? fallback;
}

/** Build the draft-text map (currency → string) from a freshly loaded list. */
function draftsFromRates(rates: CurrentExchangeRate[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const r of rates) {
    map[r.currency] = r.rateToCAD != null ? String(r.rateToCAD) : '';
  }
  return map;
}

export const ExchangeRatesPage = () => {
  const { role } = usePermissions();
  const canEdit = roleAtLeast(role, 'admin'); // raised from technician+ (RBAC audit D3)

  const [rates, setRates] = useState<CurrentExchangeRate[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingCurrency, setSavingCurrency] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const [snackbar, setSnackbar] = useState<{
    open: boolean; message: string; severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const notify = (message: string, severity: 'success' | 'error') =>
    setSnackbar({ open: true, message, severity });

  /** Replace local state from a server-provided rates list (GET/PUT/DELETE). */
  const applyRates = useCallback((next: CurrentExchangeRate[]) => {
    const sorted = sortForDisplay(next);
    setRates(sorted);
    setDrafts(draftsFromRates(sorted));
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCurrentRates();
      applyRates(data);
    } catch (err) {
      notify(errorMessage(err, 'Failed to load exchange rates'), 'error');
    } finally {
      setLoading(false);
    }
  }, [applyRates]);

  useEffect(() => { void refresh(); }, [refresh]);

  const displayRows = useMemo(() => sortForDisplay(rates), [rates]);
  const existingCurrencies = useMemo(() => rates.map((r) => r.currency), [rates]);

  /** Whether a row's draft is a valid, changed number worth saving. */
  const isDirty = (row: CurrentExchangeRate): boolean => {
    const draft = (drafts[row.currency] ?? '').trim();
    if (draft === '') return false;
    const value = Number(draft);
    if (!Number.isFinite(value)) return false;
    return value !== row.rateToCAD;
  };

  const handleDraftChange = (currency: string, value: string) =>
    setDrafts((prev) => ({ ...prev, [currency]: value }));

  const handleSave = async (currency: string) => {
    const draft = (drafts[currency] ?? '').trim();
    const value = Number(draft);
    if (!Number.isFinite(value)) { notify('rateToCAD must be a finite number', 'error'); return; }
    if (value <= 0) { notify('rateToCAD must be greater than 0', 'error'); return; }
    if (value > 100000) { notify('rateToCAD must be at most 100000', 'error'); return; }

    setSavingCurrency(currency);
    try {
      const updated = await setCurrentRate({ currency, rateToCAD: value });
      applyRates(updated);
      notify(`Saved ${currency} rate`, 'success');
    } catch (err) {
      notify(errorMessage(err, `Failed to save ${currency} rate`), 'error');
    } finally {
      setSavingCurrency(null);
    }
  };

  const handleAdd = async (currency: string, rateToCAD: number) => {
    setAddSaving(true);
    try {
      const updated = await setCurrentRate({ currency, rateToCAD });
      applyRates(updated);
      setAddOpen(false);
      notify(`Added ${currency} rate`, 'success');
    } catch (err) {
      notify(errorMessage(err, `Failed to add ${currency}`), 'error');
    } finally {
      setAddSaving(false);
    }
  };

  const requestRemove = (currency: string) => {
    setConfirmDlg({
      open: true,
      title: `Remove ${currency}?`,
      message:
        `This clears the current ${currency} rate. Historical quotes that locked ` +
        `this rate are not affected, and you can re-add it at any time.`,
      confirmLabel: 'Remove',
      confirmColor: 'error',
      onConfirm: async () => {
        setSavingCurrency(currency);
        try {
          const updated = await removeCurrencyRate(currency);
          applyRates(updated);
          notify(`Removed ${currency}`, 'success');
        } catch (err) {
          notify(errorMessage(err, `Failed to remove ${currency}`), 'error');
        } finally {
          setSavingCurrency(null);
        }
      },
    });
  };

  return (
    <Box>
      {/* Header row */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap',
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Exchange Rates</Typography>
          <Typography variant="caption" color="text.secondary">
            Current FX rate per currency, expressed as Canadian dollars per 1 unit
            ({BASE_CURRENCY} is the base, always 1.0000). New quotes pick up these rates;
            saving keeps a dated history behind the scenes.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => { void refresh(); }}
            disabled={loading}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Refresh
          </Button>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAddOpen(true)}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Add currency
            </Button>
          )}
        </Box>
      </Box>

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only access. Editing exchange rates requires a Lab Admin.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Currency</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Current rate to CAD</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Relationship</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last updated</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Read-only base row */}
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>{BASE_CURRENCY}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>1.0000</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">1 {BASE_CURRENCY} = 1.0000 {BASE_CURRENCY}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">Base currency</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="text.secondary">Read-only</Typography>
                </TableCell>
              </TableRow>

              {displayRows.map((row) => (
                <ExchangeRateRow
                  key={row.currency}
                  row={row}
                  draft={drafts[row.currency] ?? ''}
                  dirty={isDirty(row)}
                  saving={savingCurrency === row.currency}
                  canEdit={canEdit}
                  isDefault={isDefaultCurrency(row.currency)}
                  onDraftChange={(v) => handleDraftChange(row.currency, v)}
                  onSave={() => { void handleSave(row.currency); }}
                  onRemove={() => requestRemove(row.currency)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddCurrencyDialog
        open={addOpen}
        existing={existingCurrencies}
        saving={addSaving}
        onClose={() => setAddOpen(false)}
        onSubmit={(currency, rateToCAD) => { void handleAdd(currency, rateToCAD); }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'success' ? 2000 : 4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
    </Box>
  );
};
